/**
 * A document's findings: every compliance gap on it — regulatory findings and
 * gap suggestions (control amendments) — as the findings queue's rows. Open
 * findings first; decided ones fold under a disclosure. Each row opens the
 * finding's own page, which brings the reader back here.
 *
 * The decisions live on the finding page (resolve with evidence, dismiss with
 * a reason, reopen); a row's menu offers assign and dismiss for admins.
 *
 * @module components/regulation/policy/PolicyFindingsTab
 */
import { ChevronRightIcon, RadarIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../../state/orgProfile";
import { ComplianceOrganizationMissing, ComplianceUnavailable } from "../ComplianceUnavailable";
import { ListSkeleton } from "../LoadingSkeletons";
import { FindingRow, useDismissFlow, type FindingRowContext } from "../finding/FindingsQueue";
import { compareFindingItems } from "../finding/FindingsQueue.logic";
import type { FindingItem } from "../finding/findingItem";
import { useFindingItems } from "../finding/useFindingItems";
import { toastManager } from "../../ui/toast";
import { findingAwaitsReassessment } from "./PolicyReassessment.logic";

export function PolicyFindingsTab({
  organizationId,
  orgSegment,
  policyId,
  reassessing = false,
}: {
  readonly organizationId: string | null;
  /** The `$org` URL segment, for links to a finding's own page. */
  readonly orgSegment: string;
  readonly policyId: string;
  /** A re-assessment of this document is queued or running. */
  readonly reassessing?: boolean;
}) {
  const view = useFindingItems(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const dismissFlow = useDismissFlow(view.actions);
  const [showDecided, setShowDecided] = useState(false);
  const items = useMemo(
    () => view.items.filter((item) => item.policyId === policyId).sort(compareFindingItems("severity")),
    [view.items, policyId],
  );
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  if (organizationId === null) return <ComplianceOrganizationMissing />;
  if (view.unavailableReason !== null) return <ComplianceUnavailable reason={view.unavailableReason} onRetry={view.refresh} />;
  if (!view.ready) return <ListSkeleton rows={3} />;

  const open = items.filter((item) => item.status === "open");
  const decided = items.filter((item) => item.status !== "open");
  const rechecking = reassessing ? open.filter((item) => item.finding !== null && findingAwaitsReassessment(item.finding)).length : 0;
  const context: FindingRowContext = {
    orgSegment,
    canManage,
    people: view.people,
    peopleById: view.peopleById,
    selection: [],
    onSelect: () => {},
    onAssign: (targets: ReadonlyArray<FindingItem>, assigneeId: string | null) =>
      void Promise.all(targets.map((target) => view.actions.plan(target, { assigneeId }))).then((errors) => {
        const error = errors.find((candidate) => candidate !== null);
        toastManager.add(
          error
            ? { type: "error", title: "Could not assign", description: error }
            : { type: "success", title: assigneeId ? `Assigned to ${view.personName(assigneeId) ?? "them"}` : "Unassigned" },
        );
      }),
    onDismiss: (targets) => {
      if (targets.length > 0) dismissFlow.open(targets);
    },
    itemsById,
    fromPolicy: policyId,
  };

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
        <RadarIcon aria-hidden className="mx-auto size-5 text-muted-foreground/60" />
        <p className="mt-3 text-sm font-medium text-foreground">No findings on this document</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          When monitoring or Inspector finds a gap in this document, it lands here: to resolve with a newer version, or
          dismiss with a reason.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-policy-findings="">
      {rechecking > 0 ? (
        <p role="status" className="text-xs text-sky-700 dark:text-sky-300">
          Re-checking {rechecking} {rechecking === 1 ? "finding" : "findings"} against the new version…
        </p>
      ) : null}
      {open.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-border/60 divide-y divide-border/50">
          {open.map((item) => (
            <FindingRow key={item.id} item={item} context={context} showDocument={false} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border/60 px-6 py-8 text-center text-sm text-muted-foreground">
          No open findings on this document.
        </div>
      )}
      {decided.length > 0 ? (
        <section className="overflow-hidden rounded-xl border border-border/60">
          <button
            type="button"
            aria-expanded={showDecided}
            onClick={() => setShowDecided((value) => !value)}
            className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <ChevronRightIcon aria-hidden className={cn("size-3.5 transition-transform duration-150", showDecided && "rotate-90")} />
            {decided.length} decided {decided.length === 1 ? "finding" : "findings"}
          </button>
          {showDecided ? (
            <div className="divide-y divide-border/50 border-t border-border/60">
              {decided.map((item) => (
                <FindingRow key={item.id} item={item} context={context} showDocument={false} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}
      {dismissFlow.dialog}
    </div>
  );
}
